<template>
  <div class="calendar-wrapper">
    <div class="calendar-header">
      <button @click="previousMonth" class="nav-button">‹</button>
      <h3 class="month-year">{{ monthYearDisplay }}</h3>
      <button @click="nextMonth" class="nav-button">›</button>
    </div>

    <div v-if="layout === 'month-and-year'" class="month-year-selector">
      <select v-model="selectedMonth" class="selector">
        <option v-for="(month, index) in months" :key="index" :value="index">
          {{ month }}
        </option>
      </select>
      <select v-model="selectedYear" class="selector">
        <option v-for="year in yearRange" :key="year" :value="year">
          {{ year }}
        </option>
      </select>
    </div>

    <div class="calendar">
      <div class="weekdays">
        <div v-for="day in weekdays" :key="day" class="weekday">
          {{ day }}
        </div>
      </div>
      <div class="days">
        <div
          v-for="(day, index) in calendarDays"
          :key="index"
          class="day"
          :class="{
            'other-month': !day.currentMonth,
            today: day.isToday,
            selected: day.isSelected,
            disabled: isDateDisabled(day.date),
          }"
          @click="selectDate(day.date)"
        >
          {{ day.date.getDate() }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Calendar",
  props: {
    modelValue: {
      type: Date,
      default: () => new Date(),
    },
    minValue: {
      type: [Date, Object],
      default: null,
    },
    maxValue: {
      type: [Date, Object],
      default: null,
    },
    layout: {
      type: String,
      default: "month", // 'month' or 'month-and-year'
    },
  },
  emits: ["update:modelValue"],
  data() {
    const initialDate =
      this.modelValue instanceof Date ? this.modelValue : new Date();
    return {
      selectedMonth: initialDate.getMonth(),
      selectedYear: initialDate.getFullYear(),
      months: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
      weekdays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    };
  },
  computed: {
    monthYearDisplay() {
      return `${this.months[this.selectedMonth]} ${this.selectedYear}`;
    },
    yearRange() {
      let minYear = this.selectedYear - 10;
      let maxYear = this.selectedYear + 10;

      if (this.minValue) {
        const minDate = this.convertToDate(this.minValue);
        minYear = Math.max(minYear, minDate.getFullYear());
      }

      if (this.maxValue) {
        const maxDate = this.convertToDate(this.maxValue);
        maxYear = Math.min(maxYear, maxDate.getFullYear());
      }

      const years = [];
      for (let i = minYear; i <= maxYear; i++) {
        years.push(i);
      }
      return years;
    },
    calendarDays() {
      const year = this.selectedYear;
      const month = this.selectedMonth;

      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const firstDayOfWeek = firstDay.getDay();
      const lastDayOfMonth = lastDay.getDate();

      const days = [];

      // Previous month's days
      const prevMonthLastDay = new Date(year, month, 0).getDate();
      for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        days.push({
          date: new Date(year, month - 1, prevMonthLastDay - i),
          currentMonth: false,
          isToday: false,
          isSelected: false,
        });
      }

      // Current month's days
      for (let i = 1; i <= lastDayOfMonth; i++) {
        const date = new Date(year, month, i);
        const isToday =
          date.getDate() === new Date().getDate() &&
          date.getMonth() === new Date().getMonth() &&
          date.getFullYear() === new Date().getFullYear();

        let isSelected = false;
        if (this.modelValue instanceof Date) {
          isSelected =
            date.getDate() === this.modelValue.getDate() &&
            date.getMonth() === this.modelValue.getMonth() &&
            date.getFullYear() === this.modelValue.getFullYear();
        }

        days.push({
          date,
          currentMonth: true,
          isToday,
          isSelected,
        });
      }

      // Next month's days
      const remainingDays = 42 - days.length;
      for (let i = 1; i <= remainingDays; i++) {
        days.push({
          date: new Date(year, month + 1, i),
          currentMonth: false,
          isToday: false,
          isSelected: false,
        });
      }

      return days;
    },
  },
  methods: {
    convertToDate(dateValue) {
      if (dateValue instanceof Date) {
        return dateValue;
      }
      // Support for CalendarDate-like objects from @internationalized/date
      if (dateValue && typeof dateValue === "object") {
        if ("year" in dateValue && "month" in dateValue && "day" in dateValue) {
          return new Date(dateValue.year, dateValue.month - 1, dateValue.day);
        }
      }
      return new Date();
    },
    isDateDisabled(date) {
      if (this.minValue) {
        const minDate = this.convertToDate(this.minValue);
        if (date < minDate) return true;
      }
      if (this.maxValue) {
        const maxDate = this.convertToDate(this.maxValue);
        if (date > maxDate) return true;
      }
      return false;
    },
    selectDate(date) {
      if (!this.isDateDisabled(date)) {
        this.$emit("update:modelValue", date);
      }
    },
    previousMonth() {
      if (this.selectedMonth === 0) {
        this.selectedMonth = 11;
        this.selectedYear--;
      } else {
        this.selectedMonth--;
      }
    },
    nextMonth() {
      if (this.selectedMonth === 11) {
        this.selectedMonth = 0;
        this.selectedYear++;
      } else {
        this.selectedMonth++;
      }
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal instanceof Date) {
        this.selectedMonth = newVal.getMonth();
        this.selectedYear = newVal.getFullYear();
      }
    },
  },
};
</script>

<style scoped>
.calendar-wrapper {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e0e0e0);
  border-radius: 0.5rem;
  padding: 1rem;
  max-width: 400px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.nav-button {
  background: none;
  border: 1px solid var(--border, #e0e0e0);
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-size: 1.2rem;
  transition: all 0.2s;
}

.nav-button:hover {
  background: var(--surface-light, #f5f5f5);
  border-color: var(--primary_color, #1a237e);
}

.month-year {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: var(--text-primary, #333);
}

.month-year-selector {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.selector {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid var(--border, #e0e0e0);
  border-radius: 4px;
  font-size: 0.95rem;
  cursor: pointer;
  background: var(--surface, #fff);
  color: var(--text-primary, #333);
}

.selector:hover {
  border-color: var(--primary_color, #1a237e);
}

.calendar {
  width: 100%;
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.weekday {
  text-align: center;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0.5rem;
  color: var(--text-secondary, #666);
}

.days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
}

.day {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  border: 1px solid transparent;
  background: var(--surface, #fff);
  color: var(--text-primary, #333);
  transition: all 0.2s;
}

.day:not(.disabled):hover {
  background: var(--surface-light, #f5f5f5);
  border-color: var(--primary_color, #1a237e);
}

.day.other-month {
  color: var(--text-secondary, #ccc);
  background: var(--surface-light, #f9f9f9);
}

.day.today {
  border-color: #00ff00;
  border-width: 2px;
  font-weight: 600;
}

.day.selected {
  background: var(--primary_color, #1a237e);
  color: #fff;
  font-weight: 600;
}

.day.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--surface-light, #f5f5f5);
}
</style>
